import axios from "axios";
import { REFRESH_TOKEN } from "./endpoints";
import jwtDecode from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL;

// Global var on purpose
// Set when token is refreshing so that no two or more requests attempt to
// refresh at the same time, which will result in one successful request and the
// others failing. We can not use a state here since state is async
export var isRefreshing = false;

const REFRESH_WINDOW_MS = 300000; // 5 minutes

interface DecodedToken {
  nameid: string;
}

const TOKEN_KEY = "@sisantoken";
const FORGERY_TOKEN_KEY = "@sisanForgeryToken";

const getToken = () => sessionStorage.getItem(TOKEN_KEY);
const setToken = (token: string) => sessionStorage.setItem(TOKEN_KEY, token);
const removeToken = () => sessionStorage.removeItem(TOKEN_KEY);

const getForgeryToken = () => sessionStorage.getItem(FORGERY_TOKEN_KEY);
const removeForgeryToken = () => sessionStorage.removeItem(FORGERY_TOKEN_KEY);

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(
  async (config) => {
    const controller = new AbortController();
    const token = getToken();
    const forgeryToken = getForgeryToken();

    if (token && forgeryToken) {
      let newToken = null;
      const decodedToken = jwtDecode<DecodedToken>(token);
      const userId = decodedToken.nameid;

      if (
        isTokenExpiringSoon(decodedToken) &&
        config.url !== REFRESH_TOKEN &&
        !isRefreshing
      ) {
        try {
          isRefreshing = true;
          const response = await refreshToken(userId, token);
          newToken = response?.data?.token;

          if (!newToken) throw new Error();
          else setToken(newToken);
        } catch (error) {
          removeToken();
          removeForgeryToken();
          controller.abort();
        } finally {
          isRefreshing = false;
        }
      }

      config.headers["Authorization"] = `Bearer ${newToken ?? token}`;
      config.headers["X-CSRF-Token"] = forgeryToken;
    }

    return {
      ...config,
      signal: controller.signal,
    };
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response ? error.response.status : null;
    if (status === 401) {
      removeToken();
      removeForgeryToken();
      window.location.href = "/";
    }
  },
);

const isTokenExpiringSoon = (decodedToken: any) => {
  const exp: number = decodedToken.exp;
  const currMillis = Date.now();

  return currMillis >= exp * 1000 - REFRESH_WINDOW_MS;
};

const refreshToken = (userId: any, token: string) => {
  return api.post(REFRESH_TOKEN, { userId: userId, userToken: token });
};

export default api;
